
import { computed, defineComponent, inject, ref } from 'vue';
import { ISignupFormState, PASSWORD_MIN_LENGTH, passwordPolicyError } from '../signupFlow';
import { ISignupService, SignupErrorField } from '../signupService';
import { PhArrowLeft, PhArrowRight, PhLock } from '@phosphor-icons/vue';

export default defineComponent({
    name: 'StepAccount',
    components: { PhArrowLeft, PhArrowRight, PhLock },
    emits: ['next', 'back', 'edit-email'],
    setup(props, { emit }) {
        const form = inject('signupForm') as ISignupFormState;
        const service = inject('signupService') as ISignupService;
        const showErrors = ref(false);
        const submitting = ref(false);
        const serverError = ref('');
        const serverErrorField = ref<SignupErrorField | undefined>(undefined);

        // Enforced here so a weak password is caught before the Auth0 call
        // instead of coming back as a server-side rejection. The rule itself
        // lives in signupFlow.ts — it has to track the Auth0 connection policy.
        const passwordError = computed(() => passwordPolicyError(form.password));

        const confirmError = computed(() => {
            if (!form.confirmPassword) return 'This field is required.';
            if (form.password !== form.confirmPassword) return 'Passwords do not match.';
            return '';
        });

        // Auth0's own verdict on the password wins over ours while it stands:
        // its policy is the one that actually gates the account.
        const passwordMessage = computed(() => {
            if (serverErrorField.value === 'password') return serverError.value;
            return showErrors.value ? passwordError.value : '';
        });

        const stepError = computed(() =>
            serverErrorField.value === 'password' ? '' : serverError.value
        );

        const isValid = computed(
            () => !passwordError.value && !confirmError.value && form.agreedToTerms
        );

        // Creates the Auth0 user from the password typed above and advances to
        // verify-email — no redirect to the hosted signup page, so the password
        // is never asked for twice (FLOCK-6670). Stub mode is unchanged.
        async function handleNext() {
            if (!isValid.value) {
                showErrors.value = true;
                return;
            }
            submitting.value = true;
            serverError.value = '';
            serverErrorField.value = undefined;
            const result = await service.createAccount(form);
            submitting.value = false;
            if (result.success) {
                emit('next');
                return;
            }
            serverErrorField.value = result.field;
            serverError.value = result.reason || 'There was an error creating your account. Please try again.';
        }

        return {
            form,
            showErrors,
            submitting,
            confirmError,
            passwordMessage,
            passwordMinLength: PASSWORD_MIN_LENGTH,
            serverErrorField,
            stepError,
            handleNext
        };
    }
});
